import { parseVideoMomentHref, formatVideoMomentLabel, getCachedVideoTitle, fetchVideoTitle } from './videoMoment';
import { parseClipHref, getCachedClipTitle, fetchClipTitle, isClipDeleted } from './clipShare';

export const CHAT_BUBBLE_MS = 5000;
export const CHAT_BUBBLE_FADE_MS = 400;
export const CHAT_BUBBLE_MAX_WIDTH = 160;
export const CHAT_BUBBLE_MAX_LINES = 3;
const CHAT_LINK_TITLE_MAX = 48;

// Same URL finder as hall chat so timestamp/clip posts label the same way.
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+|\b(?:www\.)?bigplays\.gg(?:\/[^\s]*)?)/gi;

function truncateChatTitle(text, n) {
  const s = String(text || '');
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}

function splitUrl(raw) {
  const trailing = (raw.match(/[),.;!?]+$/) || [''])[0];
  return {
    core: trailing ? raw.slice(0, -trailing.length) : raw,
    trailing
  };
}

function hrefFor(core) {
  if (/^https?:\/\//i.test(core)) return core;
  if (/^www\./i.test(core)) return `https://${core}`;
  return `https://${core}`;
}

function isSameSite(href) {
  try {
    const host = new URL(href).hostname.replace(/^www\./i, '').toLowerCase();
    const here = (typeof window !== 'undefined' ? window.location.hostname : '')
      .replace(/^www\./i, '')
      .toLowerCase();
    return host === 'bigplays.gg' || (here && host === here);
  } catch {
    return false;
  }
}

function labelForChatLink(core) {
  const href = hrefFor(core);
  const clip = parseClipHref(href);
  if (clip && isSameSite(href)) {
    if (isClipDeleted(clip.clipId)) return 'Clip has been deleted';
    return truncateChatTitle(getCachedClipTitle(clip.clipId), CHAT_LINK_TITLE_MAX) || 'Clip';
  }
  const moment = parseVideoMomentHref(href);
  if (moment && isSameSite(href)) {
    const title = truncateChatTitle(getCachedVideoTitle(moment.videoId), CHAT_LINK_TITLE_MAX);
    return formatVideoMomentLabel(title, moment.t);
  }
  return core;
}

function forEachChatLink(body, fn) {
  const src = String(body || '');
  if (!src) return;
  URL_RE.lastIndex = 0;
  let match;
  while ((match = URL_RE.exec(src))) {
    fn(splitUrl(match[0]).core);
  }
}

function sameSitePath(href) {
  try {
    const u = new URL(href);
    return `${u.pathname || '/'}${u.search}${u.hash}`;
  } catch {
    return '/';
  }
}

export function formatChatBubbleText(body) {
  const src = String(body || '');
  if (!src) return '';
  URL_RE.lastIndex = 0;
  let last = 0;
  let out = '';
  let match;
  while ((match = URL_RE.exec(src))) {
    if (match.index > last) out += src.slice(last, match.index);
    const { core, trailing } = splitUrl(match[0]);
    out += labelForChatLink(core);
    if (trailing) out += trailing;
    last = match.index + match[0].length;
  }
  if (last < src.length) out += src.slice(last);
  return out.replace(/\s+/g, ' ').trim();
}

/** Parse hall-chat URLs into the same clip / timestamp / link targets the panel uses. */
export function chatBubbleLinks(body) {
  const links = [];
  forEachChatLink(body, (core) => {
    const href = hrefFor(core);
    const clip = parseClipHref(href);
    if (clip && isSameSite(href)) {
      links.push({
        kind: isClipDeleted(clip.clipId) ? 'deleted-clip' : 'clip',
        href,
        path: sameSitePath(href),
        clip
      });
      return;
    }
    const moment = parseVideoMomentHref(href);
    if (moment && isSameSite(href)) {
      links.push({
        kind: 'moment',
        href,
        path: sameSitePath(href),
        moment
      });
      return;
    }
    links.push({
      kind: isSameSite(href) ? 'same-site' : 'url',
      href,
      path: isSameSite(href) ? sameSitePath(href) : href
    });
  });
  return links;
}

export function actionableChatBubbleLink(links) {
  return (links || []).find((l) => (
    l && (l.kind === 'clip' || l.kind === 'moment' || l.kind === 'same-site' || l.kind === 'url')
  )) || null;
}

export function chatBubbleHitAt(hits, px, py) {
  for (let i = (hits || []).length - 1; i >= 0; i--) {
    const hit = hits[i];
    if (!hit) continue;
    if (px >= hit.x && px <= hit.x + hit.w && py >= hit.y && py <= hit.y + hit.h) return hit;
  }
  return null;
}

export function chatBubbleVariant(link, links) {
  if (link?.kind === 'clip') return 'clip';
  if (link) return 'link';
  if ((links || []).some((l) => l.kind === 'deleted-clip')) return 'deleted';
  return 'text';
}

function titleFetchesForChatBody(body) {
  const fetches = [];
  const seen = new Set();
  forEachChatLink(body, (core) => {
    const href = hrefFor(core);
    const clip = parseClipHref(href);
    if (clip && isSameSite(href) && !isClipDeleted(clip.clipId)) {
      const key = `clip:${clip.clipId}`;
      if (!seen.has(key)) {
        seen.add(key);
        fetches.push(fetchClipTitle(clip.clipId));
      }
    }
    const moment = parseVideoMomentHref(href);
    if (moment && isSameSite(href)) {
      const key = `video:${moment.videoId}`;
      if (!seen.has(key)) {
        seen.add(key);
        fetches.push(fetchVideoTitle(moment.videoId));
      }
    }
  });
  return fetches;
}

export function hydrateChatBubbleTitles(store, dto) {
  const fetches = titleFetchesForChatBody(dto?.body);
  if (!fetches.length) return Promise.resolve();
  return Promise.all(fetches).then(() => {
    if (!store || dto?.id == null) return;
    const next = formatChatBubbleText(dto.body);
    Object.keys(store).forEach((k) => {
      if (store[k] && store[k].messageId === dto.id && next) store[k].text = next;
    });
  });
}

function isSharedGuestLabel(value) {
  return String(value == null ? '' : value).trim().toLowerCase() === 'guest';
}

export function chatBubbleKeys(dto) {
  const keys = [];
  if (!dto) return keys;
  // playerId is the hall sprite id. Guests share the display name "Guest"
  // and the sentinel userId "guest", so those must not be keys or one guest's
  // message is drawn on every guest.
  if (dto.playerId != null && dto.playerId !== '' && !isSharedGuestLabel(dto.playerId)) {
    keys.push(String(dto.playerId));
  }
  if (dto.userId != null && dto.userId !== '' && !isSharedGuestLabel(dto.userId)) {
    keys.push(String(dto.userId));
  }
  if (dto.username && !isSharedGuestLabel(dto.username)) keys.push(String(dto.username));
  return keys;
}

export function rememberChatBubble(store, dto, now, durationMs = CHAT_BUBBLE_MS) {
  if (!store) return store;
  const text = formatChatBubbleText(dto?.body);
  const keys = chatBubbleKeys(dto);
  if (!text || !keys.length) return store;
  const entry = {
    text,
    until: now + durationMs,
    messageId: dto.id,
    links: chatBubbleLinks(dto?.body)
  };
  keys.forEach((k) => { store[k] = entry; });
  hydrateChatBubbleTitles(store, dto);
  return store;
}

export function forgetChatBubble(store, messageId) {
  if (!store || messageId == null) return store;
  Object.keys(store).forEach((k) => {
    if (store[k] && store[k].messageId === messageId) delete store[k];
  });
  return store;
}

export function pruneChatBubbles(store, now) {
  if (!store) return store;
  Object.keys(store).forEach((k) => {
    if (!store[k] || store[k].until <= now) delete store[k];
  });
  return store;
}

export function chatBubbleFor(store, identities, now) {
  if (!store) return null;
  for (const id of identities || []) {
    if (id == null || id === '' || isSharedGuestLabel(id)) continue;
    const bubble = store[String(id)];
    if (bubble && bubble.until > now) return bubble;
  }
  return null;
}

export function chatBubbleAlpha(now, until, fadeMs = CHAT_BUBBLE_FADE_MS) {
  const left = until - now;
  if (left <= 0) return 0;
  if (left >= fadeMs) return 1;
  return left / fadeMs;
}

export function wrapBubbleLines(text, measureWidth, maxWidth, maxLines = CHAT_BUBBLE_MAX_LINES) {
  const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return [];
  const words = cleaned.split(' ');
  const lines = [];
  let cur = '';

  const ellipsize = (s) => {
    let out = String(s || '');
    while (out.length && measureWidth(`${out}…`) > maxWidth) out = out.slice(0, -1);
    return out ? `${out}…` : '…';
  };

  const flush = (ellipsis) => {
    if (!cur) return;
    lines.push(ellipsis || measureWidth(cur) > maxWidth ? ellipsize(cur) : cur);
    cur = '';
  };

  for (let i = 0; i < words.length; i++) {
    if (lines.length >= maxLines) break;
    const word = words[i];
    const trial = cur ? `${cur} ${word}` : word;
    if (measureWidth(trial) <= maxWidth) {
      cur = trial;
      continue;
    }
    if (cur) {
      if (lines.length + 1 >= maxLines) {
        cur = trial;
        flush(true);
        return lines;
      }
      flush(false);
      i -= 1;
      continue;
    }
    lines.push(ellipsize(word));
  }
  if (cur && lines.length < maxLines) flush(false);
  return lines.slice(0, maxLines);
}

function fillRoundRect(ctx, x, y, w, h, radius) {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, radius);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.fill();
}

function drawPlayTriangle(ctx, x, y, size) {
  const h = size;
  const w = size * 0.85;
  ctx.beginPath();
  ctx.moveTo(x, y - h / 2);
  ctx.lineTo(x, y + h / 2);
  ctx.lineTo(x + w, y);
  ctx.closePath();
  ctx.fill();
}

function drawClipPillBubble(ctx, x, y, text, alpha) {
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.font = '500 13px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const measure = (s) => ctx.measureText(s).width;
  const play = 10;
  const gap = 6;
  const padX = 10;
  const boxH = 32;
  const tail = 6;
  const textMax = Math.max(24, CHAT_BUBBLE_MAX_WIDTH - padX * 2 - play - gap);
  const label = wrapBubbleLines(text, measure, textMax, 1)[0] || 'Clip';
  const boxW = Math.min(
    CHAT_BUBBLE_MAX_WIDTH,
    Math.max(boxH, padX * 2 + play + gap + measure(label))
  );
  const boxX = x - boxW / 2;
  const boxY = y - tail - boxH;

  ctx.fillStyle = '#000000';
  fillRoundRect(ctx, boxX, boxY, boxW, boxH, boxH / 2);
  ctx.beginPath();
  ctx.moveTo(x - 5, y - tail);
  ctx.lineTo(x + 5, y - tail);
  ctx.lineTo(x, y);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.shadowBlur = 0;
  const cy = boxY + boxH / 2;
  drawPlayTriangle(ctx, boxX + padX, cy, play);
  ctx.fillText(label, boxX + padX + play + gap, cy);
  ctx.restore();
  return { x: boxX, y: boxY, w: boxW, h: boxH + tail };
}

export function drawChatBubble(ctx, x, y, text, alpha, { clickable = false, variant = 'text' } = {}) {
  if (!ctx || !text || !(alpha > 0)) return null;
  if (variant === 'clip') return drawClipPillBubble(ctx, x, y, text, alpha);

  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.font = variant === 'deleted' ? 'italic 12px Arial' : 'bold 12px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const measure = (s) => ctx.measureText(s).width;
  const lines = wrapBubbleLines(text, measure, CHAT_BUBBLE_MAX_WIDTH);
  if (!lines.length) {
    ctx.restore();
    return null;
  }

  const lineH = 14;
  const padX = 8;
  const padY = 6;
  const tail = 6;
  const radius = 8;
  let innerW = 0;
  lines.forEach((ln) => { innerW = Math.max(innerW, measure(ln)); });
  const boxW = Math.min(CHAT_BUBBLE_MAX_WIDTH, innerW) + padX * 2;
  const boxH = lines.length * lineH + padY * 2;
  const boxX = x - boxW / 2;
  const boxY = y - tail - boxH;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.82)';
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(boxX, boxY, boxW, boxH, radius);
  } else {
    ctx.rect(boxX, boxY, boxW, boxH);
  }
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(x - 6, y - tail);
  ctx.lineTo(x + 6, y - tail);
  ctx.lineTo(x, y);
  ctx.closePath();
  ctx.fill();

  const asLink = clickable && variant === 'link';
  ctx.fillStyle = variant === 'deleted' ? '#9ca3af' : (asLink ? '#93c5fd' : '#ffffff');
  ctx.shadowBlur = 0;
  lines.forEach((ln, i) => {
    const ty = boxY + padY + lineH / 2 + i * lineH;
    ctx.fillText(ln, x, ty);
    if (asLink) {
      const tw = measure(ln);
      ctx.strokeStyle = 'rgba(147, 197, 253, 0.9)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - tw / 2, ty + 7);
      ctx.lineTo(x + tw / 2, ty + 7);
      ctx.stroke();
    }
  });
  ctx.restore();
  return { x: boxX, y: boxY, w: boxW, h: boxH + tail };
}
